import { useEffect, type ComponentType } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  inferNextStepFromUser,
  isPathForCurrentOnboardingStep,
  pathForNextStep,
} from "../utils/onboardingNextStep";

const withFullOnboarding = () => {
  return (WrappedComponent: ComponentType<Record<string, unknown>>) => {
    const FullOnboarding = (props: Record<string, unknown>) => {
      const { user, isFetchingUser } = useAuth();
      const navigate = useNavigate();
      const location = useLocation();
      const pathname = location.pathname.split("?")[0];

      useEffect(() => {
        if (isFetchingUser) return;

        if (!user) {
          navigate("/login", { replace: true });
          return;
        }

        if (user.password_set === 0) {
          navigate("/sign-up/create-password", { replace: true });
          return;
        }

        const step = inferNextStepFromUser(user);
        if (step && step !== "complete") {
          const target = pathForNextStep(step);
          if (
            target &&
            !isPathForCurrentOnboardingStep(step, pathname)
          ) {
            navigate(target, { replace: true });
          }
          return;
        }
        // Profile photo and username are dashboard setup tasks, not gates.
      }, [user, isFetchingUser, navigate, pathname]);

      // Hide the page only until the first profile load. A background refetch
      // (every setup task triggers one) keeps the previous user, so unmounting
      // here would throw away open modals mid-flow, such as a photo upload's
      // success step.
      if (!user) {
        return null;
      }

      if (user.password_set === 0) {
        return null;
      }

      const step = inferNextStepFromUser(user);
      if (step && step !== "complete") {
        if (isPathForCurrentOnboardingStep(step, pathname)) {
          return <WrappedComponent {...props} />;
        }
        return null;
      }

      return <WrappedComponent {...props} />;
    };

    return FullOnboarding;
  };
};

export default withFullOnboarding;
